import { z } from 'zod'

export const UserRoleSchema = z.enum([
  'ADMIN',
  'BUSINESS',
  'CUSTOMER',
  'ADVANCED_USER',
])

const NullableStringSchema = z.string().nullish()
const nullToUndefined = (value: string | null | undefined) => value ?? undefined

const roleFromOpenApiUser = (
  role?: string,
  roles?: string[],
  roleNames?: string | string[],
): z.infer<typeof UserRoleSchema> => {
  const roleTokens = [
    ...(role ? [role] : []),
    ...(roles ?? []),
    ...(Array.isArray(roleNames) ? roleNames : roleNames ? [roleNames] : []),
  ]
  const roleText = roleTokens.join(',')
  const roleSet = new Set(roleTokens.map((item) => item.toUpperCase()))

  if (!roleText) return 'CUSTOMER'
  if (
    roleSet.has('ADMIN') ||
    roleSet.has('ROOT') ||
    roleSet.has('SUPER_ADMIN') ||
    /系统管理员|超级管理员|管理员/.test(roleText)
  ) {
    return 'ADMIN'
  }
  if (roleSet.has('BUSINESS') || /业务|内部员工|员工/.test(roleText)) {
    return 'BUSINESS'
  }
  if (
    roleSet.has('ADVANCED_USER') ||
    /高级用户/.test(roleText)
  ) {
    return 'ADVANCED_USER'
  }
  if (roleSet.has('EXTERNAL_USER')) return 'CUSTOMER'
  if (roleSet.has('CUSTOMER') || /客户/.test(roleText)) return 'CUSTOMER'
  return 'CUSTOMER'
}

export const UserSchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),
    userId: z.union([z.string(), z.number()]).optional(),
    username: NullableStringSchema,
    nickname: NullableStringSchema,
    name: NullableStringSchema,
    email: NullableStringSchema,
    mobile: NullableStringSchema,
    phone: NullableStringSchema,
    avatar: NullableStringSchema,
    role: z.string().optional(),
    roles: z.array(z.string()).optional(),
    roleNames: z.union([z.string(), z.array(z.string())]).optional(),
    perms: z.array(z.string()).optional(),
    status: z.union([z.enum(['active', 'disabled']), z.number()]).optional(),
    createTime: NullableStringSchema,
    createdAt: NullableStringSchema,
    authorizedUntil: NullableStringSchema,
  })
  .transform((value) => ({
    id: String(value.id ?? value.userId),
    name: value.name ?? value.nickname ?? value.username ?? '用户',
    email: nullToUndefined(value.email),
    phone: nullToUndefined(value.phone ?? value.mobile),
    avatar: nullToUndefined(value.avatar),
    role: roleFromOpenApiUser(value.role, value.roles, value.roleNames),
    status:
      value.status === 0 || value.status === 'disabled'
        ? ('disabled' as const)
        : ('active' as const),
    createdAt: value.createdAt ?? value.createTime ?? new Date().toISOString(),
    authorizedUntil: nullToUndefined(value.authorizedUntil),
  }))

export type UserSchemaType = z.infer<typeof UserSchema>
